/**
 * veodiagram-issues#41 — modal for reviewing + correcting routes
 * and URLs the static analyzer couldn't fully resolve.
 *
 * Opens AFTER analysis. Lists every (filePath, line, kind) where
 * the resolver produced a placeholder; the user fills in the
 * actual value or accepts the placeholder as-is. Persisted via
 * PUT /projects/:id/route-url-overrides; the next analyze
 * substitutes user values everywhere downstream.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '../../services/api';

type Kind = 'route' | 'apiCallUrl' | 'externalHttpUrl';

interface UnresolvedEntry {
  filePath: string;
  line: number;
  kind: Kind;
  autoValue: string;
  existingOverride: {
    userValue: string;
    autoValue: string | null;
    reason: string | null;
  } | null;
  drifted: boolean;
}

interface Props {
  isOpen: boolean;
  projectId: string;
  onClose: () => void;
  /** Called after a successful save so callers can refresh badges. */
  onSaved?: (writtenCount: number) => void;
}

const KIND_LABEL: Record<Kind, string> = {
  route: 'Server route',
  apiCallUrl: 'API call URL',
  externalHttpUrl: 'External HTTP URL',
};

const KIND_BADGE: Record<Kind, string> = {
  route: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  apiCallUrl: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  externalHttpUrl: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

export function RouteUrlReviewModal({ isOpen, projectId, onClose, onSaved }: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<UnresolvedEntry[]>([]);
  // Map: `${filePath}:${line}:${kind}` → user-entered value.
  // R1#6 — drafts persist across close/reopen via a ref keyed by
  // projectId, so a user who accidentally closes the modal doesn't
  // lose their in-flight edits.
  const draftsByProjectRef = useRef<Map<string, Map<string, string>>>(new Map());
  const [drafts, setDrafts] = useState<Map<string, string>>(new Map());
  const [filterKind, setFilterKind] = useState<Kind | 'all'>('all');
  // R1#4 — synchronous save guard. React state updates lag behind
  // click events; rapid double-clicks were able to fire
  // `saveRouteUrlOverrides` twice before the disable rendered.
  const saveInFlightRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const resp = await api.listUnresolvedRouteUrls(projectId);
        if (cancelled) return;
        setEntries(resp.entries);
        // Seed drafts: prefer the per-project ref (preserves
        // in-flight edits across close/reopen); fall back to
        // existingOverride values for first open.
        const carryover = draftsByProjectRef.current.get(projectId) ?? new Map<string, string>();
        const seeded = new Map<string, string>();
        for (const e of resp.entries) {
          const key = keyOf(e);
          if (carryover.has(key)) {
            seeded.set(key, carryover.get(key)!);
          } else if (e.existingOverride) {
            seeded.set(key, e.existingOverride.userValue);
          }
        }
        setDrafts(seeded);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load unresolved entries');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId]);

  // R1#6 — persist current drafts to the projectId-keyed ref so
  // close/reopen preserves them. Runs on every drafts change.
  useEffect(() => {
    draftsByProjectRef.current.set(projectId, new Map(drafts));
  }, [drafts, projectId]);

  const filteredEntries = useMemo(() => {
    if (filterKind === 'all') return entries;
    return entries.filter(e => e.kind === filterKind);
  }, [entries, filterKind]);

  const summary = useMemo(() => {
    const byKind: Record<Kind, number> = { route: 0, apiCallUrl: 0, externalHttpUrl: 0 };
    for (const e of entries) byKind[e.kind]++;
    return { total: entries.length, byKind };
  }, [entries]);

  const setDraft = useCallback((entry: UnresolvedEntry, value: string) => {
    setDrafts(prev => {
      const next = new Map(prev);
      next.set(keyOf(entry), value);
      return next;
    });
  }, []);

  const markAsFine = useCallback((entry: UnresolvedEntry) => {
    // "Accept the placeholder as the canonical value" — write
    // userValue = autoValue so the entry stops appearing in the
    // unresolved list.
    setDrafts(prev => {
      const next = new Map(prev);
      next.set(keyOf(entry), entry.autoValue);
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    // R1#4 — synchronous guard against double-click. setSaving's
    // re-render is async; without the ref a rapid second click
    // fires the PUT twice.
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    setSaving(true);
    setError(null);
    const toWrite: Array<{
      filePath: string;
      line: number;
      kind: Kind;
      autoValue: string | null;
      userValue: string;
    }> = [];
    for (const e of entries) {
      const v = drafts.get(keyOf(e));
      if (typeof v === 'string' && v.length > 0) {
        toWrite.push({
          filePath: e.filePath,
          line: e.line,
          kind: e.kind,
          autoValue: e.autoValue,
          userValue: v,
        });
      }
    }
    try {
      const resp = await api.saveRouteUrlOverrides(projectId, toWrite);
      // Clear the per-project draft buffer after a successful save —
      // the next open should re-seed from the freshly-persisted state.
      draftsByProjectRef.current.delete(projectId);
      // R2#4 — close FIRST, then notify. If the caller's onSaved
      // throws (e.g. its refresh fetch fails), the modal still
      // closes cleanly — the user's data WAS persisted, so the
      // modal staying open with a confusing error would be wrong.
      onClose();
      try {
        onSaved?.(resp.written);
      } catch (err) {
        console.warn('[route-url review] onSaved callback threw:', err);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
      setSaving(false);
    } finally {
      saveInFlightRef.current = false;
    }
  }, [projectId, entries, drafts, onClose, onSaved]);

  // R2#2 — Escape closes the modal. Keyboard users get parity with
  // backdrop click. Bound at the document level since the modal
  // root isn't focusable by default.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="route-url-review-title"
        className="relative z-10 w-full max-w-4xl mx-4 max-h-[85vh] bg-background border border-border rounded-lg shadow-lg flex flex-col"
      >
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <h2 id="route-url-review-title" className="text-lg font-semibold">
              Routes &amp; URLs needing review
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {loading
                ? 'Loading…'
                : `${summary.total} unresolved ${pluralize(summary.total, 'entry', 'entries')} — ${summary.byKind.route} ${pluralize(summary.byKind.route, 'route')}, ${summary.byKind.apiCallUrl} API ${pluralize(summary.byKind.apiCallUrl, 'call')}, ${summary.byKind.externalHttpUrl} external HTTP`}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              The analyzer couldn't statically resolve these paths. Fill in the actual value, or
              "Mark as fine" to accept the placeholder.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-2 border-b border-border flex gap-2 text-sm">
          <span className="text-muted-foreground">Filter:</span>
          {(['all', 'route', 'apiCallUrl', 'externalHttpUrl'] as const).map(k => (
            <button
              key={k}
              type="button"
              onClick={() => setFilterKind(k as typeof filterKind)}
              className={`px-2 py-0.5 rounded ${
                filterKind === k
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-accent'
              }`}
            >
              {k === 'all' ? 'All' : KIND_LABEL[k as Kind]}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded text-sm">
              {error}
            </div>
          )}
          {loading ? (
            <div className="text-center text-muted-foreground py-8">Loading entries…</div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              {entries.length === 0
                ? 'No unresolved entries — every route/URL was resolved statically.'
                : 'No entries match the current filter.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-2">Kind</th>
                  <th className="py-2 pr-2">File:Line</th>
                  <th className="py-2 pr-2">Auto-extracted</th>
                  <th className="py-2 pr-2">Your value</th>
                  <th className="py-2 pr-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map(e => {
                  const k = keyOf(e);
                  const draft = drafts.get(k) ?? '';
                  return (
                    <tr key={k} className="border-b border-border/50 hover:bg-accent/30">
                      <td className="py-2 pr-2">
                        <span className={`px-1.5 py-0.5 text-xs rounded ${KIND_BADGE[e.kind]}`}>
                          {KIND_LABEL[e.kind]}
                        </span>
                      </td>
                      <td className="py-2 pr-2 font-mono text-xs">
                        <div className="truncate max-w-[260px]" title={e.filePath}>
                          {shortenPath(e.filePath)}:{e.line}
                        </div>
                        {e.drifted && (
                          <div className="text-amber-600 dark:text-amber-400 text-xs mt-0.5">
                            source changed
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-2 font-mono text-xs text-muted-foreground">
                        {e.autoValue}
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          type="text"
                          value={draft}
                          onChange={ev => setDraft(e, ev.target.value)}
                          placeholder="actual value…"
                          className="w-full px-2 py-1 border border-border rounded bg-background text-xs"
                        />
                      </td>
                      <td className="py-2">
                        {/* R2#10 — disable "Fine" when autoValue is
                            empty (typical for externalHttpUrl whose
                            URL wasn't extractable). The PUT
                            validator rejects userValue=""; the user
                            must type a real value instead. */}
                        <button
                          type="button"
                          onClick={() => markAsFine(e)}
                          disabled={e.autoValue.length === 0}
                          className="text-xs px-2 py-1 border border-border rounded hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            e.autoValue.length === 0
                              ? 'No auto-extracted value to accept; type the actual URL'
                              : 'Accept the auto value as-is'
                          }
                        >
                          Fine
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm border border-border rounded hover:bg-accent disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading || drafts.size === 0}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : `Save ${drafts.size} ${drafts.size === 1 ? 'override' : 'overrides'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function keyOf(e: { filePath: string; line: number; kind: Kind }): string {
  return `${e.filePath}:${e.line}:${e.kind}`;
}

// R2#9 — small pluralization helper; defaults to '+s' suffix for
// regular words, supports an explicit plural form for irregulars
// ('entry' → 'entries').
function pluralize(n: number, singular: string, plural?: string): string {
  if (n === 1) return singular;
  return plural ?? `${singular}s`;
}

function shortenPath(p: string): string {
  // Drop the clone tmpdir prefix for compact display.
  const cloneIdx = p.indexOf('/veodiagram-clone-');
  if (cloneIdx >= 0) {
    const after = p.slice(cloneIdx + '/veodiagram-clone-'.length);
    const slashIdx = after.indexOf('/');
    return slashIdx >= 0 ? after.slice(slashIdx + 1) : after;
  }
  return p;
}
